import { z } from "zod";

import { unlistedDiscriminantSchema } from "../../../shared/core/discriminated-union-read.schema";

/**
 * Run the tenant's on-demand backup NOW — the operator lever for
 * `VIBE_TENANT_BACKUP_STALE`, `VIBE_TENANT_DR_COPY_STALE` and
 * `VIBE_TENANT_BACKUP_JOB_FAILING` (NEX-3137). Dispatches the SAME
 * `StartBackupJob`/`StartCopyJob` AWS Backup calls the nightly plan and the
 * teardown capture already use — into the tenant's regular vault, so the
 * result is indistinguishable from a normal nightly backup to every existing
 * freshness read. One click, two independent dispatches (see
 * `RunTenantBackupNowUseCase`'s own doc comment for why the copy is separate
 * from the backup rather than chained).
 */
export const AdminVibeTenantClusterRunBackupNowBodySchema = z
  .object({
    organizationId: z.string().min(1),
    /**
     * Why the operator is running this. Required, same discipline as
     * force-converge's reason — this shows up on the audit trail as the
     * human-readable answer to "why did this tenant get an extra backup job".
     */
    reason: z.string().min(1).max(500)
  })
  .strict();

export const BackupDispatchOutcomeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("started"), backupJobId: z.string().nullable() }),
  z.object({ kind: z.literal("failed"), reason: z.string() })
]);

/** `BackupDispatchOutcome` as a reader parses it: a `kind` a newer backend added reads as the server's own word. */
const BackupDispatchOutcomeReadSchema = z.union([
  BackupDispatchOutcomeSchema,
  z.looseObject({ kind: unlistedDiscriminantSchema(BackupDispatchOutcomeSchema) })
]);

export const CopyDispatchOutcomeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("started"), recoveryPointArn: z.string() }),
  z.object({ kind: z.literal("failed"), reason: z.string() }),
  z.object({ kind: z.literal("no_local_point_yet") }),
  // Distinct from `no_local_point_yet`: the listing FAILED, so nothing about
  // "genuinely empty" is known. See `RunTenantBackupNowUseCase`'s doc comment.
  z.object({ kind: z.literal("could_not_check_local_point"), reason: z.string() }),
  z.object({ kind: z.literal("no_dr_region") })
]);

/** `CopyDispatchOutcome` as a reader parses it: a `kind` a newer backend added reads as the server's own word. */
const CopyDispatchOutcomeReadSchema = z.union([
  CopyDispatchOutcomeSchema,
  z.looseObject({ kind: unlistedDiscriminantSchema(CopyDispatchOutcomeSchema) })
]);

/** What the press did for ONE protected resource class. */
export const TenantBackupNowTargetOutcomeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("dispatched"),
    backup: BackupDispatchOutcomeReadSchema,
    copy: CopyDispatchOutcomeReadSchema
  }),
  // Distinct from the cluster-level `could_not_verify_protected_resource`: the
  // listing SUCCEEDED and simply holds nothing this target claims.
  z.object({ kind: z.literal("no_protected_resource") })
]);

/** `TenantBackupNowTargetOutcome` as a reader parses it: a `kind` a newer backend added reads as the server's own word. */
const TenantBackupNowTargetOutcomeReadSchema = z.union([
  TenantBackupNowTargetOutcomeSchema,
  z.looseObject({ kind: unlistedDiscriminantSchema(TenantBackupNowTargetOutcomeSchema) })
]);

const TenantBackupNowTargetResultSchema = z.object({
  /** The target's stable key — matches `VibeTenantBackupTarget.key`. */
  target: z.string(),
  /** Human-readable subject, so an operator reading two results can tell them apart. */
  label: z.string(),
  outcome: TenantBackupNowTargetOutcomeReadSchema
});

/**
 * Discriminated outcome. Every variant is a SUCCESS — mirrors
 * `RunTenantBackupNowOutcome` in the backend use case. A failed AWS dispatch is
 * still a legitimate answer (e.g. "already running"), not a hard error; only a
 * failure to even read the cluster row is `{ success: false }` at the HTTP
 * layer. A read that fails on AWS (protected-resource listing, newest-point
 * lookup) is still a SUCCESS envelope here, carrying its own distinct
 * `could_not_verify_protected_resource` / `could_not_check_local_point` kind —
 * never folded into the sibling "genuinely nothing there" kind.
 *
 * 🔴 `ran` CARRIES A LIST, AND IT DOES SO EVEN WHILE THERE IS ONE TARGET. The
 * outcome used to be a single `dispatched` holding one backup and one copy,
 * because the use case took `VIBE_TENANT_BACKUP_TARGETS[0]`. A wire shape that
 * cannot express two answers is what would have let a second resource class be
 * dropped silently while the response still read "dispatched".
 */
export const AdminVibeTenantClusterRunBackupNowOutcomeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("ran"),
    targets: z.array(TenantBackupNowTargetResultSchema)
  }),
  z.object({ kind: z.literal("not_found") }),
  // Cluster-level: ONE listing call covers the whole tenant, so its failure says
  // nothing about any individual target. See `RunTenantBackupNowUseCase`.
  z.object({ kind: z.literal("could_not_verify_protected_resource"), reason: z.string() })
]);

/** `AdminVibeTenantClusterRunBackupNowOutcome` as a reader parses it: a `kind` a newer backend added reads as the server's own word. */
export const AdminVibeTenantClusterRunBackupNowOutcomeReadSchema = z.union([
  AdminVibeTenantClusterRunBackupNowOutcomeSchema,
  z.looseObject({
    kind: unlistedDiscriminantSchema(AdminVibeTenantClusterRunBackupNowOutcomeSchema)
  })
]);

/**
 * Body for the operator's "run the restore drill now" lever (NEX-3195).
 *
 * ⚠️ NOT THE SAME KIND OF BUTTON AS `RunBackupNow`, despite the identical shape.
 * That one dispatches into vaults AWS already manages. This one starts a real
 * `StartRestoreJob`, which creates a REAL, BILLED EBS volume holding a copy of
 * the tenant's git repositories — one that AWS will never delete, because its
 * automatic cleanup applies only to restores a Restore Testing Plan performed.
 * The control plane owns that volume's whole life from here (see
 * `SettleOperatorRestoreDrillsUseCase`), which is why pressing this arms a
 * background poller instead of finishing inside the request.
 */
export const AdminVibeTenantClusterRunRestoreDrillNowBodySchema = z
  .object({
    organizationId: z.string().min(1),
    /**
     * Why the operator is running this. Required, same discipline as
     * force-converge and run-backup-now — this is the human-readable answer to
     * "why did this tenant get an unscheduled restore" on the audit trail, and
     * an unscheduled restore costs money and copies tenant data.
     */
    reason: z.string().min(1).max(500)
  })
  .strict();

/**
 * Discriminated outcome. Mirrors `RunTenantRestoreDrillNowOutcome` in the
 * backend use case.
 *
 * Every variant is a SUCCESS envelope: AWS refusing, a vault with nothing in it,
 * and a tenant that has not converged its drill stack are all legitimate answers
 * an operator must read verbatim, not 500s. Only a failure to read the cluster
 * row is `{ success: false }` at the HTTP layer.
 *
 * 🔴 `dispatched_but_untracked` IS NOT A COSMETIC VARIANT AND MUST NEVER BE
 * RENDERED AS SUCCESS. It means a restore IS running and the row that would have
 * made it findable did not save — so nothing will validate it and nothing will
 * delete the volume it creates. The job id rides out because it is the only
 * handle anyone has left on it.
 *
 * And note that `could_not_check_recovery_point` is kept distinct from
 * `no_recovery_point`, exactly as the backup lever keeps `could_not_check_local_point`
 * distinct from `no_local_point_yet`: "I could not look" is not "there is
 * nothing there".
 */
export const TenantRestoreDrillNowTargetOutcomeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("dispatched"),
    restoreJobId: z.string(),
    recoveryPointArn: z.string()
  }),
  z.object({
    kind: z.literal("dispatched_but_untracked"),
    restoreJobId: z.string(),
    reason: z.string()
  }),
  z.object({ kind: z.literal("failed"), reason: z.string() }),
  // No plan, no validator, no restore role — and converging will not produce
  // one, because the provisioner does not declare a drill for this target yet.
  // Distinct from `drill_not_converged`, whose remedy is a converge.
  z.object({ kind: z.literal("drill_not_wired") }),
  // 🔴 THE OPPOSITE CLAIM TO `drill_not_wired`, WHICH IS WHY IT CANNOT REUSE IT.
  // A drill DOES run for this target and DOES prove it; what is missing is a
  // by-hand path, because this lever is EBS-shaped end to end — it reads a
  // recovery point's volume type and size and passes an availability-zone
  // override to `StartRestoreJob`, and a target whose drill declares no
  // read-back validator declares no availability zone because it needs none.
  // Reporting it as `drill_not_wired` would tell an operator mid-incident that
  // nothing can prove the target restorable, which is false and points the
  // investigation at the provisioner instead of at AWS.
  z.object({ kind: z.literal("drill_has_no_operator_path") }),
  z.object({ kind: z.literal("drill_not_converged"), missing: z.array(z.string()) }),
  z.object({ kind: z.literal("no_protected_resource") }),
  z.object({ kind: z.literal("no_recovery_point") }),
  z.object({ kind: z.literal("could_not_check_recovery_point"), reason: z.string() }),
  // The point exists but AWS would not say how to restore it. Distinct from
  // `failed`: nothing was dispatched, and an EBS restore needs the volume type
  // and size, not just a zone — so dispatching anyway would be malformed.
  z.object({ kind: z.literal("could_not_read_restore_metadata"), reason: z.string() }),
  z.object({ kind: z.literal("already_running"), restoreJobId: z.string() })
]);

/** `TenantRestoreDrillNowTargetOutcome` as a reader parses it: a `kind` a newer backend added reads as the server's own word. */
const TenantRestoreDrillNowTargetOutcomeReadSchema = z.union([
  TenantRestoreDrillNowTargetOutcomeSchema,
  z.looseObject({ kind: unlistedDiscriminantSchema(TenantRestoreDrillNowTargetOutcomeSchema) })
]);

const TenantRestoreDrillNowTargetResultSchema = z.object({
  /** The target's stable key — matches `VibeTenantBackupTarget.key`. */
  target: z.string(),
  /** Human-readable subject, so an operator reading two results can tell them apart. */
  label: z.string(),
  outcome: TenantRestoreDrillNowTargetOutcomeReadSchema
});

export const AdminVibeTenantClusterRunRestoreDrillNowOutcomeSchema = z.discriminatedUnion("kind", [
  /**
   * 🔴 A LIST, AND HERE IT MATTERS MORE THAN ON THE BACKUP LEVER. The outcome
   * used to be a single `dispatched` carrying one restore job id, produced from
   * `VIBE_TENANT_BACKUP_TARGETS[0]` — so a second resource class would have been
   * skipped while the operator read a job id and reasonably concluded the thing
   * they clicked about was being restored.
   */
  z.object({
    kind: z.literal("ran"),
    targets: z.array(TenantRestoreDrillNowTargetResultSchema)
  }),
  z.object({ kind: z.literal("not_found") }),
  // Cluster-level: ONE listing call covers the whole tenant, so its failure says
  // nothing about any individual target.
  z.object({ kind: z.literal("could_not_verify_protected_resource"), reason: z.string() })
]);

/** `AdminVibeTenantClusterRunRestoreDrillNowOutcome` as a reader parses it: a `kind` a newer backend added reads as the server's own word. */
export const AdminVibeTenantClusterRunRestoreDrillNowOutcomeReadSchema = z.union([
  AdminVibeTenantClusterRunRestoreDrillNowOutcomeSchema,
  z.looseObject({
    kind: unlistedDiscriminantSchema(AdminVibeTenantClusterRunRestoreDrillNowOutcomeSchema)
  })
]);

export type AdminVibeTenantClusterRunBackupNowBody = z.infer<
  typeof AdminVibeTenantClusterRunBackupNowBodySchema
>;
export type AdminVibeTenantClusterRunBackupNowOutcome = z.infer<
  typeof AdminVibeTenantClusterRunBackupNowOutcomeSchema
>;
export type AdminVibeTenantClusterRunRestoreDrillNowBody = z.infer<
  typeof AdminVibeTenantClusterRunRestoreDrillNowBodySchema
>;
export type AdminVibeTenantClusterRunRestoreDrillNowOutcome = z.infer<
  typeof AdminVibeTenantClusterRunRestoreDrillNowOutcomeSchema
>;
